import React from "react";

import styles from "../../../../styles/integrations.module.scss";

import { EnvironmentStorage } from "../../../tools/EnvironmentStorage";
import { Loading } from "../../../components/Loading";
import { Drawer } from "../../../components/Drawer";
import { Button } from "../../../components/Button";
import { Context } from "pages/Integrations";
import { EnvironmentsAPI } from "providers/EnvironmentsAPI";
import { Toggle } from "shared/components/Toggle";
import { Input, Select } from "@components";
import { IntegrationsAPI } from "providers/IntegrationsAPI";
import AtenaAIStoreDrawer from "./AtenaAIStoreDrawer";
import AtenaAICatalogDrawer from "./AtenaAICatalogDrawer";
import AtenaAICheckoutDrawer from "./AtenaAICheckoutDrawer";
import AtenaAIPaymentDrawer from "./AtenaAIPaymentDrawer";

const Steps = {
  STORE: {
    key: "STORE",
    name: "Loja",
  },
  CATALOG: {
    key: "CATALOG",
    name: "Cardápio",
  },
  CHECKOUT: {
    key: "CHECKOUT",
    name: "Caixa",
  },
  PAYMENT: {
    key: "PAYMENT",
    name: "Pagamento",
  },
};

export default class AtenaAIDrawer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingPage: true,
      loading: false,
      active: false,
      environment: "PRODUCTION",
      storeId: null,
      pricelistId: null,
      checkoutId: null,
      methods: {},
      openPayment: false,
      stores: [],
      catalogs: [],
      payments: [],
      checkouts: [],
      step: null,
    };
  }

  componentDidMount() {
    this.loadConfigs();
  }

  async loadConfigs() {
    try {
      const environmentId = EnvironmentStorage.getEnvironment().id;

      const configsResponse = await IntegrationsAPI.getAtenaAIConfigurations(
        environmentId
      );

      const atenaaiResponses = await Promise.all([
        IntegrationsAPI.getAtenaAIStores(environmentId),
        IntegrationsAPI.getAtenaAICatalogs(environmentId),
        IntegrationsAPI.getAtenaAIPayments(environmentId),
        IntegrationsAPI.getAtenaAICheckouts(environmentId),
      ]);

      const [stores, catalogs, payments, checkouts] = atenaaiResponses.map(
        (response) => response.getData([])
      );

      const configs = await configsResponse.getData({});

      this.setState({
        active: configs?.active || false,
        environment: configs?.environment || "SANDBOX",
        storeId: configs?.storeId,
        pricelistId: configs?.pricelistId,
        checkoutId: configs?.checkoutId,
        methods: configs?.methods || {},
        stores,
        catalogs,
        payments,
        checkouts,
      });

      this.setState({
        loadingPage: false,
      });
    } catch (err) {
      console.log(err);
    }
  }

  async updateConfigs(configs = {}) {
    const environmentId = EnvironmentStorage.getEnvironment().id;

    const configsResponse = await IntegrationsAPI.getAtenaAIConfigurations(
      environmentId
    );

    const currentConfigs = configsResponse.getData({});

    const newConfigs = {
      environment: "PRODUCTION",
      ...currentConfigs,
      ...configs,
    };

    console.log("AtenaAI.configs.update", { configs, newConfigs });

    const updateResponse = await IntegrationsAPI.upsertAtenaAIConfigurations(
      environmentId,
      newConfigs
    );

    if (updateResponse.getErrors([]).length > 0) {
      throw new Error(updateResponse.getErrors()[0]);
    }
  }

  buildSelectOptions(options, selectedValue) {
    const builtOptions = options.map((option) => ({
      label: option.name,
      value: option.id,
      selected: option.id === selectedValue,
    }));

    return [
      {
        label: "Selecione",
        value: null,
        selected: !selectedValue,
      },
      ...builtOptions,
    ];
  }

  buildName(options, selectedValue) {
    const option = (options || []).find(
      (option) => option.id === selectedValue
    );

    if (!option) {
      return "Não configurado";
    }

    return `${option.id} - ${option.name}`;
  }

  render() {
    const {
      loading,
      loadingPage,
      step,
      active,
      environment,
      storeId,
      pricelistId,
      checkoutId,
      methods,
      stores,
      catalogs,
      payments,
      checkouts,
    } = this.state;
    const { visible, onClose } = this.props;

    const isValid = environment && storeId && pricelistId && checkoutId;

    const catalogName = this.buildName(catalogs, pricelistId);
    const checkoutName = this.buildName(checkouts, checkoutId);
    const storeName = this.buildName(stores, storeId);

    return (
      <Drawer
        visible={visible}
        onClose={onClose}
        closeInFade
        title={"Atena.ai"}
        footer={
          <Button
            design="primary"
            className="w-100"
            onClick={() => this.updateConfigs({ active })}
            loading={loading}
            disabled={loading || !isValid}
          >
            Salvar
          </Button>
        }
      >
        <Context />

        {loadingPage ? (
          <div className="d-flex justify-center align-center h-100">
            <Loading fullScreen />
          </div>
        ) : (
          <div className={styles.scrollContainer}>
            <div className={styles.toggle}>
              <div className="d-flex justify-end w-100">
                <Toggle
                  active={active}
                  onChange={(active) => this.setState({ active })}
                />
              </div>
            </div>

            <Select
              design="two"
              label="Ambiente"
              className="mb-20"
              value={environment}
              disabled
              options={[
                {
                  label: "Produção",
                  value: "PRODUCTION",
                  selected: environment === "PRODUCTION",
                },
              ]}
            />

            <Input
              design="two"
              className="mb-20"
              floatLabel="Loja"
              value={storeName}
              disabled
            />

            <Input
              design="two"
              className="mb-20"
              floatLabel="Cardápio"
              value={catalogName}
              disabled
            />

            <Input
              design="two"
              className="mb-20"
              floatLabel="Caixa"
              value={checkoutName}
              disabled
            />

            <Button
              design="primary"
              onClick={() => this.setState({ step: Steps.STORE.key })}
              className="w-100 mt-15"
            >
              Editar
            </Button>
          </div>
        )}

        <AtenaAIStoreDrawer
          visible={step === Steps.STORE.key}
          stores={stores}
          storeId={storeId}
          onClose={() => this.setState({ step: null })}
          onSave={async (storeId) => {
            const hasChange = storeId !== this.state.storeId;

            if (hasChange) {
              await this.updateConfigs({
                storeId,
                ...(hasChange
                  ? {
                      pricelistId: null,
                      methods: null,
                      checkoutId: null,
                      catalog: null,
                    }
                  : {}),
              });
              await this.loadConfigs();
            }

            this.setState({
              step: Steps.CATALOG.key,
            });
          }}
        />

        <AtenaAICatalogDrawer
          visible={step === Steps.CATALOG.key}
          catalogs={catalogs}
          catalogId={pricelistId}
          onClose={() => this.setState({ step: null })}
          onSave={async (catalogId) => {
            const hasChange = catalogId !== this.state.pricelistId;

            if (hasChange) {
              await this.updateConfigs({ pricelistId: catalogId });
              await this.loadConfigs();
            }

            this.setState({ step: Steps.CHECKOUT.key });
          }}
        />

        <AtenaAICheckoutDrawer
          visible={step === Steps.CHECKOUT.key}
          checkouts={checkouts}
          checkoutId={checkoutId}
          onClose={() => this.setState({ step: null })}
          onSave={async (checkoutId) => {
            const hasChange = checkoutId !== this.state.checkoutId;

            if (hasChange) {
              await this.updateConfigs({ checkoutId });
              await this.loadConfigs();
            }

            this.setState({ step: Steps.PAYMENT.key });
          }}
        />

        <AtenaAIPaymentDrawer
          visible={step === Steps.PAYMENT.key}
          payments={payments}
          methods={methods}
          onClose={() => this.setState({ step: null })}
          onSave={async (methods) => {
            await this.updateConfigs({ methods });
            await this.loadConfigs();
            this.setState({ step: null });
          }}
        />
      </Drawer>
    );
  }
}
